import { useEffect, useRef, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useApp } from '../../context/useApp'
import { _, getThankYouPage } from '../../utils/helpers'
import {
	LANG,
	metrics,
	rumba,
	REDSYS_MERCHANT,
	REDSYS_TERMINAL,
	REDSYS_CURRENCY,
	REDSYS_URL,
	REDSYS_JS,
	DEFAULT_DIALOG_HANDLE,
} from '../../utils/symbols'

const autoCloseIframeOnSuccess = false
const autoCloseIframeOnError = false

const REDSYS_ERRORS: Record<string, string> = {
	'0001': 'Error interno del sistema de pago',
	'0002': 'Error en los datos de la transacción',
	'0007': 'Tarjeta caducada',
	'0008': 'Tarjeta no válida',
	'0012': 'Saldo insuficiente',
	'0014': 'Operación denegada por el banco',
	'0021': 'Tarjeta no autorizada',
	'0022': 'Límite de tarjeta excedido',
	'0024': 'Operación cancelada',
	'0028': 'Tarjeta retenida',
	'0042': 'Código de seguridad incorrecto',
	'0043': 'Tarjeta bloqueada',
	'0051': 'Fondos insuficientes',
	'0062': 'Tarjeta restringida',
	'0091': 'Banco emisor no disponible',
	'0099': 'Error en el pago',
	'0101': 'Autenticación denegada',
	'0184': 'Error en la autenticación del titular',
	'0191': 'Fecha de caducidad errónea',
	'0202': 'Tarjeta bloqueada por seguridad',
	'0502': 'Error de comunicación',
	'0503': 'Tiempo de espera agotado',
	'0912': 'Banco emisor no disponible',
	'0999': 'Error desconocido',
}

window.validationFunction = (...args: unknown[]) => {
	console.log(...args)
	return true
}

interface RedsysData {
	signature: string
	params: string
}

let scriptLoaded = false

const loadRedsysScript = (): Promise<void> => {
	if (scriptLoaded) return Promise.resolve()
	return new Promise((resolve, reject) => {
		const script = document.createElement('script')
		script.src = REDSYS_JS
		script.async = true
		script.onload = () => {
			scriptLoaded = true
			resolve()
		}
		script.onerror = () => reject(new Error('Failed to load Redsys script'))
		document.head.appendChild(script)
	})
}

export const RedsysInPagePayment = () => {
	const {
		state: { booking, venueData, screen, dialogHandles },
	} = useApp()

	const redsysFormRef = useRef<HTMLFormElement>(null)
	const [redsysData, setRedsysData] = useState<RedsysData>({ signature: '', params: '' })
	const [redsysKey, setRedsysKey] = useState(0)
	const redsysLocatorRef = useRef('')
	const dialogHandle = dialogHandles.get('app-dialog') ?? DEFAULT_DIALOG_HANDLE

	const summary = booking.getBookingSummary()
	const amount = summary.total
	const locator = booking.locator

	const checkRedsysPolling = useCallback(async () => {
		// con este comportamiento, el polling no se utiliza para redirigir al usuario, si no para métricas.
		const response = await rumba.getRedsysPolling(locator)
		if (response.result === 'ok' && response.paid) {
			// TODO: send the booking to the backend to ensure it is persisted
			// * El endpoint de redsys notify ya recupera la reserva, así que creo que no es necesario.
			await metrics.paymentEvent(booking.getBookingDataByReference())
			// *set to true to compare behavior
			if (autoCloseIframeOnSuccess) {
				setRedsysData({ signature: '', params: '' })
				window.location.href = getThankYouPage()
			}
		} else if (response.result === 'nok') {
			// Rumba error (e.g: tables occupied, already paid...)
			setRedsysData({ signature: '', params: '' })
			setRedsysKey((k) => k + 1)
			dialogHandle.openDialog({
				dialog: 'error',
				className: 'error w-100 h-100 df aic jcc',
				message: response.message ?? _('0ge_anune__'),
				button: _('0ge_under__'),
				callback: () => dialogHandle.closeDialog(),
			})
		} else if (response.result === 'ok') {
			// continue polling
			setTimeout(checkRedsysPolling, 3000)
		} else {
			metrics.paymentErrorEvent(`Redsys ${response.result}`, booking.getBookingDataByReference())
			// *set to true to compare behavior
			if (autoCloseIframeOnError) {
				setRedsysData({ signature: '', params: '' })
				setRedsysKey((k) => k + 1)
				dialogHandle.openDialog({
					dialog: 'error',
					className: 'error w-100 h-100 df aic jcc',
					message: `${REDSYS_ERRORS[response.result] ?? _('0ge_anune__')} (${response.result})`,
					button: _('0ge_under__'),
					callback: () => dialogHandle.closeDialog(),
				})
			}
		}
	}, [booking, locator, dialogHandle])

	const handleSubmit = useCallback(async () => {
		await metrics.paymentEvent(booking.getBookingDataByReference())
		redsysFormRef.current?.submit()
		setTimeout(() => checkRedsysPolling(), 3000)
	}, [booking, checkRedsysPolling])

	const receiveMessage = useCallback(
		async (event: MessageEvent) => {
			window.storeIdOper && window.storeIdOper(event, 'token', 'errorCode', window.validationFunction)
			console.log('Received message from Redsys:', redsysLocatorRef.current, event.data)
			if (!event.data?.idOper) {
				//console.error('=>>>>>', redsysLocatorRef.current, event.data)
				return
			}
			const response = await rumba.getRedsysSignature(
				event.data.idOper,
				booking,
				redsysLocatorRef.current,
				amount,
			)
			if (response.result !== 'ok' || !response.signature || !response.params) {
				console.error(response.result)
				setRedsysData({ signature: '', params: '' })
				setRedsysKey((k) => k + 1)
				dialogHandle.openDialog({
					dialog: 'error',
					contactUs: true,
					className: 'error w-100 h-100 df aic jcc',
					message: response.message ?? _('0ge_anune__'),
					button: _('0ge_under__'),
					callback: () => dialogHandle.closeDialog(),
				})
				return
			}

			setRedsysData({ signature: response.signature, params: response.params })
		},
		[amount, booking, dialogHandle],
	)

	useEffect(() => {
		loadRedsysScript()
			.then(() => {
				redsysLocatorRef.current =
					locator.replaceAll('-', '').substring(0, 9) + (100 + Math.floor(Math.random() * 1000))
				console.log('Redsys script loaded', redsysLocatorRef.current)
				const container = document.getElementById('redsys-in-site-card')
				if (container) container.innerHTML = ''
				getInSiteForm(
					'redsys-in-site-card',
					`width: 100%; color: #fff; background-color: ${venueData?.colors.brand}; border: none; font-size: 18px; font-weight: 500; cursor: pointer; height: 49px; border-radius: 6px;`, // buttonStyle
					'padding: 0 4px;', // bodyStyle
					'width: 100%; border: 2px solid #1d1e1f; margin-bottom: 4px; border-radius: 6px;', // boxStyle
					'',
					`${_('0ar_click_t')} ${booking.getTotalAmount()}&#8364;`, //€
					REDSYS_MERCHANT,
					REDSYS_TERMINAL,
					redsysLocatorRef.current,
					LANG.toUpperCase(),
					false,
					false,
					'inline',
				)
			})
			.catch((err) => console.error(err))
		return () => {
			const container = document.getElementById('redsys-in-site-card')
			if (container) container.innerHTML = ''
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [locator, redsysKey])

	useEffect(() => {
		window.addEventListener('message', receiveMessage)
		return () => window.removeEventListener('message', receiveMessage)
	}, [])

	useEffect(() => {
		if (!redsysData.signature || !redsysData.params) return
		handleSubmit()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [redsysData])

	const height = screen.width > 1052 || (screen.width <= 1000 && screen.width > 560) ? '160px' : '200px'

	return (
		<>
			<div className="redsys-in-site">
				<div id="redsys-in-site-card" style={{ height }}></div>
				<input type="hidden" id="token" />
				<input type="hidden" id="errorCode" />
				{createPortal(
					<form
						ref={redsysFormRef}
						action={`${REDSYS_URL}/realizarPago`}
						method="POST"
						target="redsys-3ds-iframe"
					>
						<input type="hidden" name="DS_SIGNATUREVERSION" value="HMAC_SHA256_V1" />
						<input type="hidden" name="DS_MERCHANTPARAMETERS" value={redsysData.params} />
						<input type="hidden" name="DS_SIGNATURE" value={redsysData.signature} />
					</form>,
					document.body,
				)}
				<div className={`redsys-iframe-wrapper ${redsysData.signature ? 'open' : 'closed'}`}>
					<iframe title="Iframe Redsys" id="redsys-3ds-iframe" name="redsys-3ds-iframe"></iframe>
				</div>
				<div className="loading-overlay">
					<span className="loader"></span>
				</div>
			</div>
		</>
	)
}
